import {Button, IconButton, Stack} from "@mui/material";
import {useEffect, useMemo, useRef, useState} from "react";
import ClientScopesFilterBar from "./ClientScopeFilter";
import {FiTrash2} from "react-icons/fi";
import {scopesColumns} from "../../constants/dataGridColumns";
import ListView from "../ListView/ListView";
import useApiRequest from "../../hooks/useApiRequest";
import useConfirmDialog from "../../hooks/useConfirmDialog";
import ConfirmDialog from "../ui/ConfirmDialog";
import ScopeSearchAutocomplete from "../Scopes/ScopeSearchAutocomplete";
import {scopeToString} from "../../utils/formatData";
import {snackbar} from "../../utils/snackbar";

const normalizeArray = (value) => {
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value)) return value;
  return [];
};

const ClientScopesGrid = ({clientId}) => {
  const [isSettingNewScope, setIsSettingNewScope] = useState(false);
  const [selectedScope, setSelectedScope] = useState(null);
  const lastSearchParams = useRef({});

  const {data, loading, list, remove, create} = useApiRequest({
    baseUrl: `/clients/${clientId}/scopes`,
    resource: "clients",
    field: "scopes"
  });

  const {data: dataAssignedScopes, list: listAssignedScopes} = useApiRequest({
    baseUrl: `/clients/${clientId}/scopes`,
    resource: "clients",
    field: "scopes"
  });

  const handleSearch = async (params) => {
    lastSearchParams.current = params;
    await list(params);
  };

  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async (id) => {
    const {success} = await remove(id);
    if (!success) return;

    snackbar.success("Scope desasignado del cliente correctamente.");
    await list(lastSearchParams.current);
    await listAssignedScopes();
  });

  useEffect(() => {
    listAssignedScopes();
    // eslint-disable-next-line
  }, [clientId]);

  const assignedScopeIds = useMemo(() => {
    return normalizeArray(dataAssignedScopes).map((scope) => scope.id);
  }, [dataAssignedScopes]);

  const handleAccept = async () => {
    if (!selectedScope?.id) return;

    const {success} = await create({scope_id: selectedScope.id});
    if (!success) return;

    snackbar.success("Scope asignado al cliente correctamente.");
    setSelectedScope(null);

    await listAssignedScopes();
    await list(lastSearchParams.current);
  };

  const handleScopeSelect = (option) => {
    setSelectedScope(option);
  };

  const handleOpenDeleteDialog = (id) => {
    const found = Array.isArray(data) ? data.find((scope) => scope.id === id) : null;
    openDialog(id, (found && scopeToString(found)) || "");
  };

  const columns = [
    ...scopesColumns,
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="delete"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDeleteDialog(params.row.id);
          }}
        >
          <FiTrash2 />
        </IconButton>
      )
    }
  ];

  return (
    <>
      <ClientScopesFilterBar clientId={clientId} onSearch={handleSearch} />
      <ListView
        viewTitle={"Scopes"}
        columns={columns}
        data={normalizeArray(data || [])}
        loading={loading}
        topRightButtons={
          <Stack direction="row" spacing={2}>
            {isSettingNewScope && (
              <Stack direction="row" spacing={2}>
                <ScopeSearchAutocomplete
                  value={selectedScope}
                  onChange={handleScopeSelect}
                  excludeIds={assignedScopeIds}
                />
                <Button variant="contained" onClick={handleAccept} disabled={!selectedScope?.id}>
                  Aceptar
                </Button>
              </Stack>
            )}
            <Button
              variant="contained"
              color={isSettingNewScope ? "error" : "primary"}
              onClick={() => {
                setSelectedScope(null);
                setIsSettingNewScope(!isSettingNewScope);
              }}
            >
              {isSettingNewScope ? "Cancelar" : "Asignar scope"}
            </Button>
          </Stack>
        }
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="scope"
        entityLabel={itemName}
      />
    </>
  );
};

export default ClientScopesGrid;
